import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "../../convex/_generated/api";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Loading, Empty, ScreenBoundary } from "../components/states";
import {
  ActionBar,
  Button,
  CollapsibleSection,
  Footnote,
  SectionHeading,
  plural,
  relativeDay,
} from "../components/kit";
import { useBusyAction } from "../lib/useBusyAction";
import { useViewer } from "../lib/viewer";
import { presentLevelsBasis, TONE_CLASSES } from "@shared/status";

/**
 * The weekly count. **One tap plus one number, per item, and nothing else.**
 *
 * v2 rises or falls here. The person holding the phone is a dental assistant,
 * standing, between patients, with ninety-odd items to get through — and the
 * recorded v1 failure of exactly this shape is receiving's ~36 taps, where a
 * per-line panel turned one delivery into a dozen submissions. `countcheck`
 * measures this screen the way `clickcount` measures the request flow, so the
 * design is structural rather than aspirational:
 *
 * - **Every number input is in the scrolling list.** No expand step, no
 *   per-row confirm, no dialog, no detail screen. The thing you tap is the
 *   thing you type into.
 * - **Count-by-exception.** Each row arrives pre-filled with what we believe is
 *   on that shelf, and an untouched row counts as its pre-fill. A correct
 *   expectation costs zero interactions, which is the only way ninety items is
 *   a two-minute job.
 * - **Zero navigation between items.** Sections default open, so a full count
 *   needs no section taps at all; folding is sugar for someone who wants it.
 * - **One mutation, at the end.** An operatory has one bar of signal. A design
 *   that writes per row is a design that fails halfway through the shelf.
 *
 * The draft is mirrored to localStorage, keyed by clinic and week, so a patient
 * walking in mid-count costs nothing. The week key comes from the server
 * (`counts.status`), never computed here: the client and the server disagreeing
 * about which week this is means either a clinic that counted is told it has
 * not, or a second count is refused for the wrong reason.
 *
 * No money anywhere — `counts.sheet` returns none, by design, and this screen
 * asks for none.
 */

type SheetRow = FunctionReturnType<typeof api.counts.sheet>[number];

/** The lower of the two ceilings the server enforces (`counts.countedQty`). */
const MAX_COUNT = 10_000;

export function Count() {
  /* Attempt-keyed, like Request: Convex throws during render, so the only way
     to re-run a failed query is to remount the subtree. */
  const [attempt, setAttempt] = useState(0);
  return (
    <ScreenBoundary
      key={attempt}
      title="The count sheet could not load"
      heading={<SectionHeading title="Weekly count" />}
      onRetry={() => setAttempt((n) => n + 1)}
    >
      <CountScreen />
    </ScreenBoundary>
  );
}

function CountScreen() {
  /* The shell has already resolved the viewer; a screen never re-queries it. */
  const viewer = useViewer();
  /* An owner or a warehouse admin has no shelf to count, and both `sheet` and
     `status` refuse them server-side. Skipping is not the permission boundary —
     that is `requireCounter` in convex/counts.ts — it is what keeps a wrong
     turn showing an explanation instead of an error card. */
  const counting = viewer.clinicId !== null;
  const status = useQuery(api.counts.status, counting ? {} : "skip");
  const sheet = useQuery(api.counts.sheet, counting ? {} : "skip");
  const submitCount = useMutation(api.counts.submit);
  const { run, busy } = useBusyAction();

  /**
   * The draft: **only the rows somebody has touched.**
   *
   * Lazily defaulted, the way Receive keys its line state — an untouched row
   * resolves to `expectedQty` at read time. Pre-populating ninety entries on
   * mount would make "differ from expected" unanswerable and would write a
   * ninety-key blob to localStorage before the assistant had done anything.
   */
  const [draft, setDraft] = useState<Record<string, number>>({});

  /* Set true while a submit is taking unusually long — the operatory-with-one-
     bar case the whole one-mutation architecture exists for. Convex retries a
     dropped mutation rather than rejecting, so without this the button reads
     "Sending…" forever and the good failure copy never fires. This does not
     reject or re-enable (a second tap could double-file); it just tells the
     truth: the numbers are saved and will go when the signal comes back. */
  const [slow, setSlow] = useState(false);

  /* Armed only when a non-empty draft is restored from localStorage, so an
     interruption returns you to where you were rather than to item 1 of 90.
     Never fires on live typing — that would yank the view around mid-count. */
  const pendingScroll = useRef(false);

  /* Clinic and week both come off the server payload. `null` while the status
     query is in flight, which is also what stops the restore below from
     running against a key we are only guessing at. */
  const storageKey =
    status && !status.submitted
      ? `nexora-count-${status.clinicId}-${status.weekKey}`
      : null;
  const restored = useRef<string | null>(null);

  useEffect(() => {
    if (!storageKey || restored.current === storageKey) return;
    restored.current = storageKey;
    try {
      const saved = window.localStorage.getItem(storageKey);
      if (!saved) return;
      const parsed: unknown = JSON.parse(saved);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const entries = Object.entries(parsed as Record<string, unknown>).filter(
          ([, value]) => typeof value === "number" && Number.isFinite(value),
        ) as Array<[string, number]>;
        if (entries.length > 0) {
          setDraft(Object.fromEntries(entries));
          pendingScroll.current = true;
        }
      }
    } catch {
      /* A corrupt or unavailable store must not take the screen down — the
         worst case is a count started from the pre-fills, which is the state
         the screen opens in anyway. */
    }
  }, [storageKey]);

  useEffect(() => {
    /* Only ever writes. Clearing is the submit path's job: an empty draft on
       first paint is indistinguishable from "nothing typed yet", and removing
       the key on that pass would delete the very entries the effect above is
       one render away from restoring. */
    if (!storageKey || Object.keys(draft).length === 0) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(draft));
    } catch {
      /* Private mode, quota, a locked-down device. The count still submits. */
    }
  }, [draft, storageKey]);

  /* After a restore, scroll to the furthest-progressed touched row — where the
     shelf-walk had got to — once the sheet has painted. Runs at most once per
     restore; `sheet` in the deps re-runs it when the query lands. */
  useEffect(() => {
    if (!pendingScroll.current || sheet === undefined || sheet.length === 0) {
      return;
    }
    const touched = new Set(Object.keys(draft));
    let lastId: string | null = null;
    for (const row of sheet) if (touched.has(row.itemId)) lastId = row.itemId;
    pendingScroll.current = false;
    if (lastId) {
      document
        .querySelector(`[data-count-item="${lastId}"]`)
        ?.scrollIntoView({ block: "center" });
    }
  }, [sheet, draft]);

  /**
   * The one handler every row is given, with no dependencies and the row as an
   * argument — which is what makes `memo` on `CountRow` worth anything. Written
   * inline it would be a fresh function per row per render, and ninety rows
   * would reconcile on every keystroke in any one of them.
   */
  const setCounted = useCallback((row: SheetRow, next: number) => {
    setDraft((current) => ({ ...current, [row.itemId]: next }));
  }, []);

  /* Grouped by subcategory — the order things sit on the shelf, which is the
     order the sheet already arrives in (`counts.sheet` sorts subcategory then
     name), so this is a walk rather than a re-sort. */
  const groups = useMemo(() => {
    const out: Array<{ name: string; rows: SheetRow[] }> = [];
    for (const row of sheet ?? []) {
      const name = row.subcategory || "Uncategorised";
      const last = out[out.length - 1];
      if (last && last.name === name) last.rows.push(row);
      else out.push({ name, rows: [row] });
    }
    return out;
  }, [sheet]);

  if (!counting) {
    return (
      <div data-screen-state="ready" className="space-y-4">
        <SectionHeading title="Weekly count" />
        <Empty
          title="A count belongs to a clinic"
          body="Counts are filed per clinic, by the people standing in front of the shelf. Your account is not attached to one, so there is nothing here to count. The compliance view shows which clinics have counted this week."
        />
      </div>
    );
  }

  if (status === undefined || sheet === undefined) {
    return (
      <div data-screen-state="loading" className="space-y-4">
        <SectionHeading title="Weekly count" />
        <Loading rows={6} label="Loading this week's count sheet" />
      </div>
    );
  }

  /* ---- already counted -------------------------------------------------- */

  /**
   * One count per clinic per week, and the server refuses the second one. So
   * the screen does not offer an edit path: a button that always fails is worse
   * than no button, and a correction is a conversation with the manager rather
   * than a silent rewrite of the week that already fired a top-up.
   */
  if (status.submitted) {
    const counted = status.itemsCounted ?? 0;
    const below = status.belowFloorCount ?? 0;
    return (
      <div
        data-screen-state="ready"
        data-count-state="submitted"
        className="space-y-4"
      >
        <SectionHeading
          title="Weekly count"
          subtitle={`${viewer.clinicName ?? "Your clinic"} · week ${status.weekKey}`}
        />
        <Empty
          tone="good"
          title="This week's count is in"
          body={
            `${counted} ${plural(counted, "item")} counted, ` +
            (below === 0
              ? "nothing below floor. "
              : `${below} below floor. `) +
            `Submitted by ${status.submittedByName ?? "someone at your clinic"} ` +
            `${status.submittedAt ? relativeDay(status.submittedAt) : "this week"}. ` +
            (status.topUpRequestId
              ? "A top-up order is with your manager for approval."
              : "Nothing needed topping up.")
          }
        />
        <Footnote>
          One count per week, per clinic. Next week's sheet opens on Monday — if
          something is wrong with this one, tell your manager rather than
          counting again.
        </Footnote>
      </div>
    );
  }

  /* ---- nothing to count ------------------------------------------------- */

  if (sheet.length === 0) {
    return (
      <div data-screen-state="ready" className="space-y-4">
        <SectionHeading
          title="Weekly count"
          subtitle={`${viewer.clinicName ?? "Your clinic"} · week ${status.weekKey}`}
        />
        <Empty
          title="No items on this clinic's carry list yet"
          body="A count sheet is built from what this clinic carries. Your manager sets that up on Stock levels — once items are on the list, with a floor and a target, they appear here every week."
        />
      </div>
    );
  }

  /* ---- the sheet -------------------------------------------------------- */

  const countedOf = (row: SheetRow) => draft[row.itemId] ?? row.expectedQty;
  const belowFloor = sheet.filter((row) => countedOf(row) < row.floorQty).length;
  const differing = sheet.filter((row) => countedOf(row) !== row.expectedQty).length;
  const draftLevels = sheet.filter((row) => row.levelsBasis === "draft").length;
  /* Never null for `draft` — the null arm is `confirmed`, which draws nothing. */
  const draftBasis = presentLevelsBasis("draft");

  const send = () =>
    run(
      async () => {
        /* Not a cancel — Convex keeps retrying and there is no double-file
           risk from one call. Just a twelve-second mark to stop the button
           lying that it is still working when the signal has gone. */
        const slowTimer = setTimeout(() => setSlow(true), 12_000);
        try {
          const result = await submitCount({
            /* Every active carry item, exactly once: the server requires full
               coverage, and an untouched row is a real count at its pre-fill. */
            lines: sheet.map((row) => ({
              itemId: row.itemId,
              countedQty: countedOf(row),
            })),
          });
          /* After the await, so neither runs on the failure path. A count lost
             because the network blinked is the failure that gets a tool
             abandoned — the draft survives, and the numbers are still on screen. */
          setDraft({});
          if (storageKey) {
            try {
              window.localStorage.removeItem(storageKey);
            } catch {
              /* Nothing to do about it, and the count is already filed. */
            }
          }
          return result;
        } finally {
          clearTimeout(slowTimer);
          setSlow(false);
        }
      },
      {
        failed: "Could not send the count — your numbers are safe, try again in a moment",
        /* `Counted — …` is load-bearing: `tools/countcheck.mjs` waits on
           /^Counted —/ to decide the count actually went through. */
        done: (result) => ({
          happened:
            `Counted — ${result.itemsCounted} ${plural(result.itemsCounted, "item")}, ` +
            (result.belowFloorCount === 0
              ? "nothing below floor"
              : `${result.belowFloorCount} below floor` +
                (result.topUpRequestId ? ", top-up sent for approval" : "")),
          next: result.topUpRequestId
            ? "Your manager approves the top-up; it'll show on My clinic until it arrives."
            : "That's this week's count filed. Nothing else for you to do.",
        }),
      },
    );

  return (
    /* No bottom padding. The <ActionBar> measures itself and the shell's
       <main> pads by that height. */
    <div data-screen-state="ready" data-count-state="sheet" className="space-y-3">
      <SectionHeading
        title="Weekly count"
        subtitle={`${viewer.clinicName ?? "Your clinic"} · week ${status.weekKey} · ${
          sheet.length
        } ${plural(sheet.length, "item")}`}
      />

      {/* One line, because at 390px this paragraph is a row of the list. Three
          lines of instruction pushed the first item to the fold on a screen
          whose whole job is the items. */}
      <p className="text-sm text-muted-foreground">
        Change what's wrong; anything you don't touch is counted as it stands.
      </p>

      {/* One quiet line for the whole sheet, not a tag on ninety rows. Deanna
          is bringing the real floors and targets, and levels of ours presented
          as decided is the thing that gets pointed at on a projector. */}
      {draftLevels > 0 && draftBasis ? (
        <Footnote tone="caution">
          <span className="font-semibold text-foreground">
            {draftBasis.label} levels.
          </span>{" "}
          {draftLevels === sheet.length
            ? "Every floor and target here is"
            : `${draftLevels} of ${sheet.length} floors and targets are`}{" "}
          {draftBasis.qualifier} — ours, awaiting Deanna's. The levels only
          decide what gets topped up; count the shelf as it is.
        </Footnote>
      ) : null}

      <div className="space-y-2 lg:grid lg:grid-cols-2 lg:items-start lg:gap-3 lg:space-y-0">
        {groups.map((group) => {
          const short = group.rows.filter(
            (row) => countedOf(row) < row.floorQty,
          ).length;
          return (
            <CollapsibleSection
              key={group.name}
              title={group.name}
              count={group.rows.length}
              summary={short > 0 ? `${short} below floor` : undefined}
              tone={short > 0 ? "red" : "gray"}
              /* Open, and that is the point: a section header tap is
                 navigation, and the bar is zero navigation between items.
                 Folding is available to anyone who wants it and required of
                 nobody. */
              defaultOpen
              /* Not sticky. A sticky header on a screen made of tap targets is
                 a header that lands on top of the row you are aiming at — the
                 exact occlusion `reachable` exists to catch. */
              sticky={false}
            >
              {/* A thunk, so a folded section costs nothing at all: passed as
                  elements, all ninety rows are built on every render of this
                  screen and thrown away by the closed ones. */}
              {() => (
                <div>
                  {group.rows.map((row) => (
                    <CountRow
                      key={row.itemId}
                      row={row}
                      value={countedOf(row)}
                      touched={row.itemId in draft}
                      onChange={setCounted}
                    />
                  ))}
                </div>
              )}
            </CollapsibleSection>
          );
        })}
      </div>

      <ActionBar>
        <p className="mb-2 text-xs leading-tight text-muted-foreground">
          <span className="tabular-nums">
            {differing} of {sheet.length}
          </span>{" "}
          differ from expected
          {belowFloor > 0 ? (
            <>
              {" · "}
              <span className="font-semibold text-red-700">
                <span className="tabular-nums">{belowFloor}</span> below floor
              </span>
            </>
          ) : (
            " · nothing below floor"
          )}
        </p>
        {/* Submitting is not just filing numbers — it places an order. Say so,
            so a thumb-brush at item 12 is not the first time the assistant
            learns the button also orders stock. */}
        {belowFloor > 0 ? (
          <p className="mb-2 text-xs leading-tight text-muted-foreground">
            Submitting files the count and sends your manager a top-up for the{" "}
            <span className="tabular-nums">{belowFloor}</span> below floor.
          </p>
        ) : null}
        {slow ? (
          <p className="mb-2 text-xs leading-tight text-amber-900">
            Taking longer than usual — you may be offline. Your count is saved;
            it will send when you're back on signal.
          </p>
        ) : null}
        <Button size="lg" className="w-full" disabled={busy} onClick={send}>
          {busy
            ? "Sending…"
            : `Submit count · ${sheet.length} ${plural(sheet.length, "item")}`}
        </Button>
      </ActionBar>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* One counted row                                                             */
/* -------------------------------------------------------------------------- */

/**
 * One row, memoised — and the whole bar lives in it.
 *
 * The number field is *in* the row, in the scrolling list, at the size of a
 * thumb: tapping it opens the keypad with the pre-fill selected, so replacing
 * it is one tap plus the digits. There is nothing to expand, nothing to
 * confirm, and nowhere to go.
 *
 * `memo` pays off because the props hold still: `row` comes from the query
 * payload, `value` is a number, and `onChange` is one dependency-free
 * `useCallback` shared by every row — so typing in one row re-renders one row.
 */
const CountRow = memo(function CountRow({
  row,
  value,
  touched,
  onChange,
}: {
  row: SheetRow;
  value: number;
  /** True once somebody has set this row — it is a key in the draft. Draws the
   *  "you have been here" tint so an interrupted count can be resumed by eye. */
  touched: boolean;
  onChange: (row: SheetRow, next: number) => void;
}) {
  const below = value < row.floorQty;
  /* An order-of-magnitude slip — "2000" into a box of 8 because the pack label
     reads "2000/Bx". The count is one-shot and un-editable, so the only place
     to catch it is here, and a quiet nudge costs an honest count nothing. */
  const implausible =
    value > row.floorQty &&
    value >= 50 &&
    value > Math.max(row.targetQty, row.expectedQty * 5);

  /* The field holds a *string*, so it can sit empty mid-edit instead of
     snapping to a red 0 the instant you backspace to retype. Only a real digit
     commits a number upward; the live tint still updates per keystroke because
     the commit is per keystroke. `seen` re-syncs the text when the value
     changes from the outside (a draft restore) but not for this row's own
     commit — which pre-absorbs it — so typing is never yanked out from under
     the caret. */
  const [text, setText] = useState(String(value));
  const seen = useRef(value);
  if (seen.current !== value) {
    seen.current = value;
    setText(String(value));
  }

  return (
    <div
      /* `data-count-item` mirrors Inventory's `data-item`: it is how
         `tools/countcheck.mjs` finds a row, and `[data-count-item="…"] input`
         is how it finds the one control that row has. */
      data-count-item={row.itemId}
      className={cn(
        "flex items-center gap-3 border-b px-3 py-2 last:border-0",
        /* Below floor is literally "needs ordering", which is what red means
           in this app — so it is the shared tone, in the shared delivery
           (`ring-1 ring-inset` over `TONE_CLASSES`, the same pairing `Chip`
           uses), and not a colour invented for this screen. A ring rather than
           a border because it costs no layout: a row crossing its floor
           mid-count must not nudge the ninety rows under it. */
        below && cn("ring-1 ring-inset", TONE_CLASSES.red),
        /* Touched-but-fine rows carry a quiet fill — not below-floor red, not
           interaction teal (which never means state here), just "walked". */
        touched && !below && "bg-muted/50",
      )}
    >
      <div className="min-w-0 flex-1">
        {/* Two lines on a phone. This catalogue's names differ at the *end* —
            "…Nitrile Glove Medium" against "…Nitrile Glove Large" — and
            truncating to one line cuts exactly the word that tells them
            apart. */}
        <div className="line-clamp-2 text-sm leading-tight font-medium">
          {row.name}
        </div>
        <div className="mt-0.5 truncate text-xs text-muted-foreground">
          {row.packSize}
          {/* Only on the rows it explains. "floor 4" printed on all ninety is
              ninety numbers nobody reads; beside a tinted row it is the reason
              for the tint — and red, so the word inherits the row's meaning
              instead of being the weakest text on it. */}
          {below ? (
            <span className="font-medium text-red-700">
              {" · "}below floor{" "}
              <span className="tabular-nums">{row.floorQty}</span>
            </span>
          ) : implausible ? (
            <span className="font-medium text-amber-800"> · unusually high</span>
          ) : null}
        </div>
      </div>

      {/* The registry `<Input>`, and deliberately **not** `QtyStepper`: that
          control clamps to 1, and an empty box on the shelf is the single most
          important number a count can carry. `inputMode="numeric"` brings up
          the keypad; the registry base is `text-base md:text-sm`, so this is
          16px on a phone and iOS does not zoom the sheet when it is focused. */}
      <Input
        value={text}
        onChange={(event) => {
          /* Digits only — a stray "-" or "e" never reaches the server — and
             the integer part before any "." so "1.5" reads as 1, not the "15"
             a bare \D-strip would silently produce. */
          const digits = event.target.value
            .split(/[.,]/)[0]
            .replace(/\D/g, "")
            .slice(0, 5);
          setText(digits);
          if (digits !== "") {
            const next = Math.min(MAX_COUNT, Number(digits));
            seen.current = next;
            onChange(row, next);
          }
        }}
        /* Empty is a mid-edit state, not the answer 0. On blur, an empty field
           falls back to the value it held — a cleared box that is walked away
           from cannot become a silent zero that orders stock. */
        onBlur={() => {
          if (text === "") setText(String(value));
        }}
        /* One tap has to be enough. Selecting the pre-fill means the first
           digit typed replaces it, instead of landing beside it and making the
           assistant clear the field first.
           **On click as well as on focus**, and that is not belt-and-braces:
           `focus` does not fire again on a field that already has it, so
           tapping the row you just typed into — the correction case, and the
           one a person does when they realise they misread the box — appended
           instead of replacing. Measured: pre-fill 8, tap, "2" → 2; tap again,
           "0" → **20**, a wrong count with nothing on screen to say so. A
           count field is always replaced wholesale, so caret placement is
           worth nothing here and the select is worth a wrong number. */
        onFocus={(event) => event.currentTarget.select()}
        onClick={(event) => event.currentTarget.select()}
        inputMode="numeric"
        autoComplete="off"
        aria-label={`Counted quantity of ${row.name}`}
        className={cn(
          "h-11 w-20 shrink-0 text-center text-base font-semibold tabular-nums",
          below && "border-red-300",
        )}
      />
    </div>
  );
});
